import type { AutocompleteFreeSoloValueMapping } from '@mui/material';
import type { ControllerRenderProps } from 'react-hook-form';

import { useField, useForwardingRef } from '@adflow/frontend/util-common';

import type { FormControlProps } from '../FormControl';
import { FormControl } from '../FormControl';

import type { MultiselectProps } from './Multiselect';
import { Multiselect } from './Multiselect';

export interface MultiselectWithControlProps<
	T,
	FreeSolo extends boolean | undefined = false,
>
	extends
		Pick<
			FormControlProps,
			| 'label'
			| 'helperStyle'
			| 'outerStyle'
			| 'helperText'
			| 'infoText'
			| 'infoTooltipProps'
		>,
		Omit<
			MultiselectProps<T, FreeSolo>,
			'value' | 'onChange' | 'onInputChange'
		> {
	name: string;
	defaultValue?: string;
	onInputChange?: (value: string) => void;
	onChangeHandler?: (
		value: (T | AutocompleteFreeSoloValueMapping<FreeSolo>)[],
		onChange: ControllerRenderProps['onChange']
	) => void;
	displayValue?: (
		value: ControllerRenderProps['value']
	) => (T | AutocompleteFreeSoloValueMapping<FreeSolo>)[];
	context?: Record<string, unknown>;
}

export function MultiselectWithControl<
	T,
	FreeSolo extends boolean | undefined = false,
>({
	// FormControlProps
	label,
	helperStyle,
	outerStyle,

	// Other
	name,
	defaultValue,
	onInputChange,
	onChangeHandler,
	displayValue,

	// Field Context
	context,

	inputRef: inputRefProp,
	// MultiselectProps
	...multiselectProps
}: MultiselectWithControlProps<T, FreeSolo>) {
	const {
		field: { onChange, value, ref: inputRefRhf },
		fieldState: { invalid: error },
		errorMessage,
	} = useField<ControllerRenderProps['value']>({
		name,
		defaultValue,
		...context,
	});

	const inputRef = useForwardingRef<null | HTMLInputElement>(null, [
		inputRefRhf,
		inputRefProp,
	]);

	return (
		<FormControl
			error={error}
			helperText={
				errorMessage?.length ? errorMessage : multiselectProps.helperText
			}
			label={label}
			helperStyle={helperStyle}
			outerStyle={outerStyle}
			infoText={multiselectProps.infoText}
			infoTooltipProps={multiselectProps.infoTooltipProps}
		>
			<Multiselect<T, FreeSolo>
				{...multiselectProps}
				name={name}
				inputRef={inputRef}
				value={displayValue ? displayValue(value) : (value ?? [])}
				onChange={(args) =>
					onChangeHandler ? onChangeHandler(args, onChange) : onChange(args)
				}
				onInputChange={
					onInputChange
						? (_event, newValue) => {
								onInputChange(newValue);
							}
						: undefined
				}
			/>
		</FormControl>
	);
}
